import { useCallback } from "react"
import {
  addTask,
  completeTask,
  uncompleteTask,
  deleteTask,
  moveToTrash,
  restoreFromTrash,
  cancelTask,
  scheduleTask,
  updateTaskTags,
} from "@/db/operations"
import { StartAtType, StartAtSubType, ObjectStatus } from "@/db/schema"
import { routeContainerStatus } from "@/features/project-header/projectStatusRequest"

/**
 * Hook that exposes task mutation actions.
 * Wraps db operations for use in components.
 */
export function useTaskActions() {
  const handleAddTask = useCallback(async (title: string) => {
    return addTask(title)
  }, [])

  const handleCompleteTask = useCallback(async (id: string) => {
    // A project / list row (e.g. scheduled into Today) is completed the container way
    if (await routeContainerStatus(id, ObjectStatus.Completed)) return
    await completeTask(id)
  }, [])

  const handleUncompleteTask = useCallback(async (id: string) => {
    if (await routeContainerStatus(id, ObjectStatus.Unfinished)) return
    await uncompleteTask(id)
  }, [])

  const handleDeleteTask = useCallback(async (id: string) => {
    await deleteTask(id)
  }, [])

  const handleMoveToTrash = useCallback(async (id: string) => {
    await moveToTrash(id)
  }, [])

  const handleRestoreFromTrash = useCallback(async (id: string) => {
    await restoreFromTrash(id)
  }, [])

  const handleCancelTask = useCallback(async (id: string) => {
    if (await routeContainerStatus(id, ObjectStatus.Canceled)) return
    await cancelTask(id)
  }, [])

  const handleScheduleTask = useCallback(
    async (
      id: string,
      startAt: string | undefined,
      type?: StartAtType,
      subType?: StartAtSubType,
    ) => {
      await scheduleTask(id, startAt, type, subType)
    },
    [],
  )

  const handleUpdateTags = useCallback(
    async (id: string, tags: string[]) => {
      await updateTaskTags(id, tags)
    },
    [],
  )

  return {
    addTask: handleAddTask,
    completeTask: handleCompleteTask,
    uncompleteTask: handleUncompleteTask,
    deleteTask: handleDeleteTask,
    moveToTrash: handleMoveToTrash,
    restoreFromTrash: handleRestoreFromTrash,
    cancelTask: handleCancelTask,
    scheduleTask: handleScheduleTask,
    updateTags: handleUpdateTags,
  }
}
